<script setup lang="ts">
import { Head } from "@inertiajs/vue3"
import { ref } from "vue"

import Heading from "@/components/Heading.vue"
import Icon from "@/components/Icon.vue"
import StudyForm from "@/components/benefits/StudyForm.vue"
import StudyProgress from "@/components/benefits/StudyProgress.vue"
import Badge from "@/components/custom/Badge.vue"
import { Alert } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import AppLayout from "@/layouts/AppLayout.vue"
import { dateFormatter } from "@/lib/formatters"
import { cn, formatPrice } from "@/lib/utils"
import { rootPath, studyPath } from "@/routes"
import { type BreadcrumbItem } from "@/types"
import type { StudiesShow } from "@/types/serializers"

defineProps<StudiesShow>()

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Dashboard", href: rootPath() },
  { title: "Ayuda de Estudios", href: studyPath() },
]

const showForm = ref(false)
</script>

<template>
  <Head :title="breadcrumbs[breadcrumbs.length - 1].title" />

  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="layout-container">
      <Heading
        title="Ayuda de Estudios"
        description="Formación técnica o de idiomas"
      />

      <p class="mb-4">
        Te apoyamos en tu desarrollo con una ayuda para formación técnica e
        idiomas. <br /><br />

        Envía los detalles del curso y la aprobación de tu responsable, y
        recibirás el reembolso en tu nómina. <br /><br />

        Las solicitudes aprobadas a partir del día 10 se incluirán en la nómina
        del mes siguiente
      </p>

      <Card>
        <CardHeader>
          <CardTitle class="text-lg">Presupuesto disponible 2026</CardTitle>
        </CardHeader>
        <CardContent class="space-y-4">
          <StudyProgress
            :max-amount="settings.max_amount"
            :reserved-amount="reserved_amount"
            :approved-amount="approved_amount"
            :allowed-amount="allowed_amount"
          />

          <!--<Alert>
            <Icon name="info" :class="cn('size-4 text-blue-400!')" />
            <p class="text-sm">
              Esta es una ayuda de pago único anual. El importe se incluirá en
              nómina un único mes del año tras la aprobación.
            </p>
          </Alert>-->
          <Button
            v-if="!showForm"
            size="lg"
            class="w-full"
            :disabled="allowed_amount <= 0"
            @click="showForm = true"
          >
            Solicitar Nueva ayuda
          </Button>
        </CardContent>
      </Card>

      <StudyForm
        v-if="showForm"
        :disabled="allowed_amount <= 0"
        @success="showForm = false"
        @cancel="showForm = false"
      />

      <!-- Bottom block: History -->
      <Card>
        <CardHeader>
          <CardTitle>Historial de Solicitudes</CardTitle>
          <CardDescription>
            Todas tus solicitudes de ayuda de estudios
          </CardDescription>
        </CardHeader>
        <CardContent class="space-y-3">
          <div
            v-for="item in history"
            :key="item.id"
            class="space-y-3 rounded-lg border p-4"
          >
            <div class="flex items-start justify-between gap-2">
              <div>
                <p class="text-sm font-semibold">{{ item.course_name }}</p>
                <p class="text-xs text-gray-500">{{ item.provider }}</p>
              </div>
              <Badge :status="item.status" />
            </div>
            <div
              class="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600 sm:grid-cols-4"
            >
              <div>
                <span class="text-gray-400">Tipo:</span>
                <span class="ml-1 font-medium text-gray-800">
                  {{ item.training_type }}
                </span>
              </div>
              <div>
                <span class="text-gray-400">Importe:</span>
                <span class="ml-1 font-medium text-gray-800">
                  {{ formatPrice(item.total_amount) }}
                </span>
              </div>
              <div>
                <span class="text-gray-400">Fechas:</span>
                <span class="ml-1 font-medium text-gray-800">
                  {{ dateFormatter(item.start_date, {}, "date") }} -
                  {{ dateFormatter(item.end_date, {}, "date") }}
                </span>
              </div>
              <div class="text-right">
                <span class="text-gray-400">Solicitud:</span>
                <span class="ml-1 font-medium text-gray-800">
                  {{ dateFormatter(item.created_at, {}, "dateTime") }}
                </span>
              </div>
            </div>
          </div>

          <div
            v-if="history.length === 0"
            class="py-8 text-center text-sm text-gray-400"
          >
            No tienes solicitudes registradas.
          </div>
        </CardContent>
      </Card>
    </div>
  </AppLayout>
</template>
